import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import AdminShell from "../components/AdminShell";
import { Loader2, Users, TrendingUp, FileText, LifeBuoy, X, Check, ChevronUp, ChevronDown, ChevronLeft, ChevronRight } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend, CartesianGrid } from "recharts";
import { toast } from "sonner";

const card = "rounded-2xl border border-neutral-800 bg-neutral-900 p-5";
const BAND_LABELS = {
  bankable: "Bankable 85+", investment_grade: "Investment-Grade 70–84",
  promising: "Promising 55–69", developing: "Developing 40–54",
  pre_readiness: "Pre-Readiness <40",
};
// Tier = amount the client is looking to raise (KES). Same rule as the RM portal (routers/rm.py _client_tier).
const INCOME_LABELS = {
  platinum: "Platinum — raising KES 500M+", gold: "Gold — raising KES 100M–500M",
  silver: "Silver — raising KES 50M–100M", bronze: "Bronze — raising under KES 50M",
};
const TIER_BADGE = {
  bronze: "border-amber-700/40 bg-amber-900/30 text-amber-300",
  silver: "border-slate-400/40 bg-slate-700/40 text-slate-200",
  gold: "border-yellow-400/50 bg-yellow-900/30 text-yellow-300",
  platinum: "border-[#0A8B85] bg-[#0A8B85]/15 text-[#5fd3cc]",
};
// Frozen first columns (checkbox · name · email) so they stay visible on horizontal scroll
const STICKY_0 = "sticky left-0 z-20 bg-neutral-950";
const STICKY_1 = "sticky left-[40px] z-20 bg-neutral-950";
const STICKY_2 = "sticky left-[200px] z-20 bg-neutral-950 shadow-[6px_0_8px_-6px_rgba(0,0,0,0.8)]";

// Horizontal-scroll wrapper with pinned ◀ ▶ arrows + edge fades so admins can see there's more to the right.
function ScrollX({ children, testid }) {
  const ref = useRef(null);
  const [canL, setCanL] = useState(false);
  const [canR, setCanR] = useState(false);
  const [stickyEnd, setStickyEnd] = useState(260);
  const update = () => {
    const el = ref.current; if (!el) return;
    setCanL(el.scrollLeft > 2);
    setCanR(el.scrollLeft + el.clientWidth < el.scrollWidth - 2);
    const th = el.querySelector("thead th:nth-child(3)");
    if (th) setStickyEnd(th.getBoundingClientRect().right - el.getBoundingClientRect().left);
  };
  useEffect(() => {
    update();
    const el = ref.current; if (!el) return;
    const ro = new ResizeObserver(update); ro.observe(el);
    window.addEventListener("resize", update);
    return () => { ro.disconnect(); window.removeEventListener("resize", update); };
  }, [children]);
  const by = (dx) => ref.current?.scrollBy({ left: dx, behavior: "smooth" });
  const arrow = "absolute top-1/2 z-30 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full border border-neutral-700 bg-neutral-900/95 text-white shadow-lg hover:bg-[#0A8B85] hover:border-[#0A8B85] transition-colors";
  return (
    <div className="relative">
      {canL && (
        <button type="button" onClick={() => by(-360)} aria-label="Scroll left" data-testid={`${testid}-scroll-left`}
          style={{ left: stickyEnd + 8 }} className={arrow}><ChevronLeft size={18} /></button>
      )}
      {canR && (
        <>
          <div className="pointer-events-none absolute inset-y-0 right-0 z-20 w-14 rounded-r-xl bg-gradient-to-l from-neutral-950/90 to-transparent" />
          <button type="button" onClick={() => by(360)} aria-label="Scroll right" data-testid={`${testid}-scroll-right`}
            className={`${arrow} right-2`}><ChevronRight size={18} /></button>
        </>
      )}
      <div ref={ref} onScroll={update} className="overflow-x-auto rounded-xl border border-neutral-800" data-testid={`${testid}-scroller`}>
        {children}
      </div>
    </div>
  );
}
const STICKY_H = "!bg-neutral-900";
const TICKET_LABELS = {
  document_upload: "Document Upload", ai_analysis: "AI Analysis",
  report_download: "Report Download", general: "General", other: "Other",
  bug: "Bug", onboarding: "Onboarding", billing: "Billing",
};
const BIZ_STAGES = [
  "Signed up — report not unlocked",
  "Report unlocked — business profile pending",
  "Profile complete — documents pending",
  "Documents uploaded — Capital Readiness Score pending",
  "Score generated — report not downloaded",
  "Report downloaded — journey complete",
];
const PROJ_STAGES = [
  "Signed up — report not unlocked",
  "Report unlocked — project intake pending",
  "Intake complete — documents pending",
  "Documents uploaded — Project Readiness Score pending",
  "Score generated — report not downloaded",
  "Report downloaded — journey complete",
];
// Automated founder emails (key → short label), in journey order.
const EMAIL_SEQ = [
  ["welcome", "Welcome"], ["day3", "Day 3"], ["day7", "Day 7"], ["day21", "Day 21"],
  ["youre_in", "You're in"], ["profile", "Profile"], ["upload", "Upload"], ["upload2", "Upload 2"],
  ["analyse", "Analyse"], ["score_ready", "Score ready"], ["inactive14", "Inactive 14d"],
];
const fmtD = (ts) => ts ? new Date(ts).toLocaleDateString("en-GB", { day: "numeric", month: "short" }) : null;

function Stat({ label, value, testid, breakdown }) {
  return (
    <div className="rounded-xl border border-neutral-800 bg-neutral-900 px-4 py-3" data-testid={testid}>
      <p className="text-[10px] font-bold uppercase tracking-widest text-neutral-500">{label}</p>
      <p className="mt-1 text-2xl font-extrabold text-white">{value}</p>
      {breakdown?.length > 0 && (
        <ul className="mt-2 space-y-0.5 border-t border-neutral-800 pt-2" data-testid={`${testid}-breakdown`}>
          {breakdown.map((b) => (
            <li key={b.key} className="flex items-center justify-between gap-2 text-[11px] leading-4">
              <span className="text-neutral-400">{b.label}</span>
              <span className="font-bold tabular-nums text-neutral-200">{b.count}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function Funnel({ title, stages, labels, testid }) {
  const max = Math.max(1, ...stages.map((s) => s.count));
  return (
    <div className={card} data-testid={testid}>
      <h3 className="text-sm font-bold text-white">{title}</h3>
      <div className="mt-4 space-y-3">
        {stages.map((s, i) => (
          <div key={s.stage}>
            <div className="flex items-baseline justify-between text-xs">
              <span className="font-semibold text-neutral-300">Stage {s.stage} · {labels[i]}</span>
              <span className="text-neutral-400">
                <b className="text-white">{s.count}</b>
                {s.pct_from_prev !== null && (
                  <span className="ml-2 text-[#F59E0B]">{s.pct_from_prev}% from prev</span>
                )}
              </span>
            </div>
            <div className="mt-1 h-3 rounded-full bg-neutral-800">
              <div className="h-full rounded-full bg-[#0A8B85]" style={{ width: `${(s.count / max) * 100}%` }} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function SortHeader({ label, col, sort, setSort, className = "" }) {
  const active = sort.col === col;
  return (
    <th className={"cursor-pointer select-none whitespace-nowrap px-3 py-2 text-left text-[10px] font-bold uppercase tracking-wider text-neutral-400 hover:text-white " + className}
        onClick={() => setSort({ col, dir: active && sort.dir === "asc" ? "desc" : "asc" })}
        data-testid={`sort-${col}`}>
      <span className="inline-flex items-center gap-0.5">{label}
        {active && (sort.dir === "asc" ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />)}
      </span>
    </th>
  );
}

const yn = (v) => v ? <span className="font-bold text-emerald-400">Y</span> : <span className="text-neutral-500">N</span>;

function useSortedFiltered(rows, sort, query, extraFilter) {
  return useMemo(() => {
    let out = rows;
    const q = query.trim().toLowerCase();
    if (q) out = out.filter((r) => JSON.stringify(r).toLowerCase().includes(q));
    if (extraFilter) out = out.filter(extraFilter);
    if (sort.col) {
      out = [...out].sort((a, b) => {
        const av = a[sort.col] ?? "", bv = b[sort.col] ?? "";
        const cmp = typeof av === "number" || typeof bv === "number"
          ? (av || 0) - (bv || 0)
          : String(av).localeCompare(String(bv));
        return sort.dir === "asc" ? cmp : -cmp;
      });
    }
    return out;
  }, [rows, sort, query, extraFilter]);
}


function FounderTable({ rows, stageLabels, scoreLabel, onRowClick, onDeleted, testid }) {
  // Newest sign-up first by default (user request, Sep 2026).
  const [sort, setSort] = useState({ col: "signed_up_at", dir: "desc" });
  const [q, setQ] = useState("");
  const [stageF, setStageF] = useState("");
  const [rmData, setRmData] = useState({ assignments: {}, rms: [] });
  const [rmF, setRmF] = useState("");
  const [sel, setSel] = useState(new Set());
  const [bulkRm, setBulkRm] = useState("");
  useEffect(() => {
    api.get("/admin/rms/assignments-map")
      .then((r) => setRmData(r.data))
      .catch(() => {});
  }, []);
  const setRm = async (companyId, rmId) => {
    const prev = rmData.assignments[companyId];
    setRmData((d) => ({ ...d, assignments: { ...d.assignments, [companyId]: rmId || undefined } }));
    try {
      if (rmId) await api.post(`/admin/rms/${rmId}/assign`, { company_id: companyId });
      else await api.delete(`/admin/rms/assignments/${companyId}`);
      toast.success(rmId ? "RM assigned — intro email on its way" : "RM unassigned");
    } catch (_) {
      toast.error("Couldn't update RM");
      setRmData((d) => ({ ...d, assignments: { ...d.assignments, [companyId]: prev } }));
    }
  };
  const rmCounts = useMemo(() => {
    const c = {};
    Object.values(rmData.assignments).forEach((rid) => { if (rid) c[rid] = (c[rid] || 0) + 1; });
    return c;
  }, [rmData]);
  const rmLabel = (m) => `${m.name} (${rmCounts[m.user_id] || 0})`;
  const rowRm = (r) => rmData.assignments[r.company_id];
  const extra = (stageF || rmF) ? (r) => {
    if (stageF && String(r.stage) !== stageF) return false;
    if (rmF === "unassigned" && rowRm(r)) return false;
    if (rmF === "assigned" && !rowRm(r)) return false;
    if (rmF && rmF !== "unassigned" && rmF !== "assigned" && rowRm(r) !== rmF) return false;
    return true;
  } : null;
  const filtered = useSortedFiltered(rows, sort, q, extra);
  const rowKey = (r) => r.company_id || r.user_id;
  const selectable = filtered.filter((r) => rowKey(r));
  const allSelected = selectable.length > 0 && selectable.every((r) => sel.has(rowKey(r)));
  const toggleAll = () => setSel(allSelected ? new Set()
    : new Set(selectable.map(rowKey)));
  const toggleOne = (cid) => setSel((s) => {
    const n = new Set(s); n.has(cid) ? n.delete(cid) : n.add(cid); return n;
  });
  const selectedRows = rows.filter((r) => sel.has(rowKey(r)));
  const selCompanyIds = selectedRows.filter((r) => r.company_id).map((r) => r.company_id);
  const [deleting, setDeleting] = useState(false);
  const bulkDelete = async () => {
    const targets = selectedRows.filter((r) => r.user_id);
    if (!targets.length) return;
    const preview = targets.slice(0, 5).map((r) => `• ${r.name} (${r.email})`).join("\n") + (targets.length > 5 ? `\n…and ${targets.length - 5} more` : "");
    const typed = window.prompt(
      `PERMANENTLY delete ${targets.length} founder account${targets.length === 1 ? "" : "s"}?\n\n${preview}\n\nThis removes their login, business profile, documents, scores and reports. It cannot be undone.\n\nType DELETE to confirm:`);
    if (typed !== "DELETE") { if (typed !== null) toast.error("Deletion cancelled — you must type DELETE"); return; }
    setDeleting(true);
    let ok = 0; const failed = [];
    for (const r of targets) {
      try { await api.delete(`/admin/users/${r.user_id}`); ok += 1; } catch (_) { failed.push(r.email); }
    }
    setDeleting(false);
    setSel(new Set());
    if (ok) toast.success(`Deleted ${ok} founder account${ok === 1 ? "" : "s"}`);
    if (failed.length) toast.error(`Couldn't delete: ${failed.join(", ")}`);
    onDeleted && onDeleted();
  };
  const bulkAssign = async () => {
    if (!bulkRm) { toast.error("Pick an RM first"); return; }
    if (!selCompanyIds.length) { toast.error("Selected founders have no business yet — nothing to assign"); return; }
    const rmName = (rmData.rms.find((m) => m.user_id === bulkRm) || {}).name;
    if (!window.confirm(`Assign ${selCompanyIds.length} founder${selCompanyIds.length === 1 ? "" : "s"} to ${rmName}? Each newly assigned founder will receive the RM intro email.`)) return;
    const done = [];
    for (const cid of selCompanyIds) {
      try { await api.post(`/admin/rms/${bulkRm}/assign`, { company_id: cid }); done.push(cid); } catch (_) {}
    }
    setRmData((d) => {
      const a = { ...d.assignments };
      done.forEach((c) => { a[c] = bulkRm; });
      return { ...d, assignments: a };
    });
    setSel(new Set()); setBulkRm("");
    if (done.length) toast.success(`Assigned ${done.length} to ${rmName} — intro emails on the way`);
    else toast.error("Couldn't assign — try again");
  };
  const splitEvenly = async () => {
    if (rmData.rms.length < 2) { toast.error("Need at least two RMs to split"); return; }
    const ids = selCompanyIds;
    if (!ids.length) { toast.error("Selected founders have no business yet — nothing to assign"); return; }
    const counts = {};
    rmData.rms.forEach((m) => { counts[m.user_id] = rmCounts[m.user_id] || 0; });
    const plan = {};
    for (const cid of ids) {
      const target = rmData.rms.reduce((a, b) => (counts[a.user_id] <= counts[b.user_id] ? a : b));
      plan[cid] = target.user_id;
      counts[target.user_id]++;
    }
    const summary = rmData.rms.map((m) =>
      `${m.name}: +${Object.values(plan).filter((v) => v === m.user_id).length}`).join(", ");
    if (!window.confirm(`Split ${ids.length} founder${ids.length === 1 ? "" : "s"} evenly (balancing current workloads) — ${summary}. Each newly assigned founder will receive the RM intro email. Continue?`)) return;
    const done = {};
    for (const [cid, rid] of Object.entries(plan)) {
      try { await api.post(`/admin/rms/${rid}/assign`, { company_id: cid }); done[cid] = rid; } catch (_) {}
    }
    setRmData((d) => ({ ...d, assignments: { ...d.assignments, ...done } }));
    setSel(new Set());
    const n = Object.keys(done).length;
    if (n) toast.success(`Split ${n} founder${n === 1 ? "" : "s"} across the RMs — intro emails on the way`);
    else toast.error("Couldn't assign — try again");
  };
  return (
    <div data-testid={testid}>
      <div className="mb-3 flex flex-wrap gap-2">
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Filter by name, email…"
          data-testid={`${testid}-search`}
          className="w-64 rounded-lg border border-neutral-700 bg-neutral-800 px-3 py-1.5 text-xs text-white placeholder-neutral-500 outline-none focus:border-[#0A8B85]" />
        <select value={stageF} onChange={(e) => setStageF(e.target.value)}
          data-testid={`${testid}-stage-filter`}
          className="rounded-lg border border-neutral-700 bg-neutral-800 px-2 py-1.5 text-xs text-white">
          <option value="">All stages</option>
          {[1, 2, 3, 4, 5, 6].map((s) => <option key={s} value={s}>{`Stage ${s}`}</option>)}
        </select>
        <select value={rmF} onChange={(e) => setRmF(e.target.value)}
          data-testid={`${testid}-rm-filter`}
          className="rounded-lg border border-neutral-700 bg-neutral-800 px-2 py-1.5 text-xs text-white">
          <option value="">All RM states</option>
          <option value="unassigned">Unassigned</option>
          <option value="assigned">Assigned (any RM)</option>
          {rmData.rms.map((m) => <option key={m.user_id} value={m.user_id}>{rmLabel(m)}</option>)}
        </select>
        <span className="self-center text-xs text-neutral-500">{filtered.length} of {rows.length}</span>
      </div>
      {sel.size > 0 && (
        <div className="mb-3 flex flex-wrap items-center gap-2 rounded-xl border border-[#0A8B85]/40 bg-[#0A8B85]/10 px-3 py-2" data-testid={`${testid}-bulk-bar`}>
          <span className="text-xs font-bold text-[#7BD3CE]">{sel.size} selected</span>
          <select value={bulkRm} onChange={(e) => setBulkRm(e.target.value)}
            data-testid={`${testid}-bulk-rm`}
            className="rounded-md border border-neutral-700 bg-neutral-900 px-2 py-1 text-xs text-white">
            <option value="">Assign to RM…</option>
            {rmData.rms.map((m) => <option key={m.user_id} value={m.user_id}>{rmLabel(m)}</option>)}
          </select>
          <button onClick={bulkAssign} disabled={!bulkRm}
            data-testid={`${testid}-bulk-apply`}
            className="rounded-md bg-[#0A8B85] px-3 py-1 text-xs font-bold text-white hover:bg-[#097a74] disabled:opacity-40">
            Assign
          </button>
          <button onClick={splitEvenly}
            data-testid={`${testid}-bulk-split`}
            className="rounded-md border border-[#0A8B85]/50 px-3 py-1 text-xs font-bold text-[#7BD3CE] hover:bg-[#0A8B85]/10">
            Split evenly
          </button>
          <button onClick={() => setSel(new Set())}
            data-testid={`${testid}-bulk-clear`}
            className="rounded-md border border-neutral-700 px-3 py-1 text-xs font-semibold text-neutral-400 hover:bg-neutral-800">
            Clear
          </button>
          <span className="text-[10px] text-neutral-500">Each newly assigned founder gets the RM intro email.</span>
          <button onClick={bulkDelete} disabled={deleting}
            data-testid={`${testid}-bulk-delete`}
            className="ml-auto rounded-md border border-red-500/50 bg-red-500/10 px-3 py-1 text-xs font-bold text-red-300 hover:bg-red-500/20 disabled:opacity-40">
            {deleting ? "Deleting…" : `Delete ${sel.size} account${sel.size === 1 ? "" : "s"}`}
          </button>
        </div>
      )}
      <ScrollX testid={testid}>
        <table className="w-full text-xs">
          <thead className="bg-neutral-900">
            <tr>
              <th className={`px-3 py-2 ${STICKY_0} ${STICKY_H}`}>
                <input type="checkbox" checked={allSelected} onChange={toggleAll}
                  className="h-3.5 w-3.5 accent-[#0A8B85]" data-testid={`${testid}-select-all`} />
              </th>
              <SortHeader label="Name" col="name" sort={sort} setSort={setSort} className={`${STICKY_1} ${STICKY_H} min-w-[160px]`} />
              <SortHeader label="Email" col="email" sort={sort} setSort={setSort} className={`${STICKY_2} ${STICKY_H} min-w-[220px]`} />
              <SortHeader label="Source" col="source" sort={sort} setSort={setSort} />
              <SortHeader label="Stage" col="stage" sort={sort} setSort={setSort} />
              <SortHeader label="Tier" col="income_bracket" sort={sort} setSort={setSort} />
              <SortHeader label={scoreLabel} col="score" sort={sort} setSort={setSort} />
              <SortHeader label="Unlocked" col="unlocked" sort={sort} setSort={setSort} />
              <SortHeader label="Analysis" col="analysis_done" sort={sort} setSort={setSort} />
              <SortHeader label="Credits" col="credits" sort={sort} setSort={setSort} />
              <SortHeader label="Report DL" col="report_downloaded" sort={sort} setSort={setSort} />
              <th className="px-3 py-2 text-left text-[10px] font-bold uppercase tracking-wider text-neutral-400">Relationship Manager</th>
              <SortHeader label="Signed Up" col="signed_up_at" sort={sort} setSort={setSort} />
              <SortHeader label="Last Active" col="last_activity_at" sort={sort} setSort={setSort} />
              <th className="px-3 py-2 text-left text-[10px] font-bold uppercase tracking-wider text-neutral-400">Emails Sent</th>
              <SortHeader label="Tech Issues" col="tech_issue" sort={sort} setSort={setSort} />
              <SortHeader label="Ticket" col="ticket_raised" sort={sort} setSort={setSort} />
              <th className="px-3 py-2 text-left text-[10px] font-bold uppercase tracking-wider text-neutral-400">Ticket Type</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((r, i) => (
              <tr key={r.company_id || r.email + i}
                  onClick={() => r.company_id && onRowClick(r)}
                  data-testid={`${testid}-row-${i}`}
                  className={"group border-t border-neutral-800 text-neutral-300 " + (r.company_id ? "cursor-pointer hover:bg-neutral-800/60" : "opacity-70")}>
                <td className={`px-3 py-2 ${STICKY_0} group-hover:bg-neutral-800`} onClick={(e) => e.stopPropagation()}>
                  {rowKey(r) ? (
                    <input type="checkbox" checked={sel.has(rowKey(r))}
                      onChange={() => toggleOne(rowKey(r))}
                      className="h-3.5 w-3.5 accent-[#0A8B85]"
                      data-testid={`${testid}-select-${i}`} />
                  ) : null}
                </td>
                <td className={`px-3 py-2 font-semibold text-white ${STICKY_1} group-hover:bg-neutral-800`} data-testid={`${testid}-name-${i}`}>{r.name}</td>
                <td className={`px-3 py-2 ${STICKY_2} group-hover:bg-neutral-800`} data-testid={`${testid}-email-${i}`}>{r.email}</td>
                <td className="px-3 py-2 whitespace-nowrap" data-testid={`${testid}-source-${i}`}>
                  {r.source && r.source !== "Direct"
                    ? <span className="rounded-full bg-[#F59E0B]/15 px-2 py-0.5 text-[11px] font-bold text-[#F59E0B]" title="Came in through a partner tenant portal">Tenant · {r.source}</span>
                    : <span className="text-neutral-500">Direct</span>}
                </td>
                <td className="px-3 py-2 whitespace-nowrap" data-testid={`${testid}-stage-${i}`}>
                  <span className="rounded-full bg-[#0A8B85]/15 px-2 py-0.5 font-bold text-[#7BD3CE]">S{r.stage}</span>
                  <span className="ml-2 text-[11px] text-neutral-400">{stageLabels[r.stage - 1]}</span>
                </td>
                <td className="px-3 py-2" data-testid={`${testid}-tier-${i}`} title={INCOME_LABELS[r.income_bracket] || "No raise amount recorded yet"}>
                  {r.income_bracket && r.income_bracket !== "unknown"
                    ? <span className={`inline-block rounded-full border px-2 py-0.5 text-[10px] font-bold capitalize ${TIER_BADGE[r.income_bracket] || ""}`}>{r.income_bracket}</span>
                    : <span className="text-neutral-600">—</span>}
                </td>
                <td className="px-3 py-2 font-bold text-white">{r.score ?? "N/A"}</td>
                <td className="px-3 py-2" data-testid={`${testid}-unlocked-${i}`} title={r.unlocked_at ? `Unlocked ${fmtD(r.unlocked_at)}` : undefined}>
                  {r.unlocked ? <span className="font-semibold text-emerald-400">{r.free_access ? "Free" : "Yes"}</span> : <span className="text-neutral-500">No</span>}
                </td>
                <td className="px-3 py-2" data-testid={`${testid}-analysis-${i}`}>
                  {r.analysis_done ? <span className="font-semibold text-emerald-400">Complete</span> : <span className="text-neutral-500">Pending</span>}
                </td>
                <td className="px-3 py-2 tabular-nums">{r.credits ?? 0}</td>
                <td className="px-3 py-2">{r.report_downloaded ? "Yes" : "No"}</td>
                <td className="px-3 py-2" onClick={(e) => e.stopPropagation()} data-testid={`${testid}-rm-${i}`}>
                  {r.company_id ? (
                    <select value={rmData.assignments[r.company_id] || ""}
                      onChange={(e) => setRm(r.company_id, e.target.value)}
                      className={"max-w-[130px] rounded-md border bg-neutral-900 px-1.5 py-1 text-[11px] outline-none focus:border-[#0A8B85] " +
                        (rmData.assignments[r.company_id] ? "border-[#0A8B85]/50 font-semibold text-[#7BD3CE]" : "border-neutral-700 text-neutral-500")}
                      data-testid={`${testid}-rm-select-${i}`}>
                      <option value="">— Unassigned —</option>
                      {rmData.rms.map((m) => <option key={m.user_id} value={m.user_id}>{rmLabel(m)}</option>)}
                    </select>
                  ) : "—"}
                </td>
                <td className="px-3 py-2 whitespace-nowrap text-neutral-300" data-testid={`founder-signed-up-${r.company_id || r.email}`}>{fmtD(r.signed_up_at) || "—"}</td>
                <td className="px-3 py-2 whitespace-nowrap text-neutral-300">{fmtD(r.last_activity_at) || "—"}</td>
                <td className="px-3 py-2" data-testid={`${testid}-emails-${i}`}>
                  <div className="flex max-w-[260px] flex-wrap gap-1">
                    {EMAIL_SEQ.filter(([k]) => r.emails?.[k]).map(([k, l]) => (
                      <span key={k} title={`${l} · ${fmtD(r.emails[k])}`} className="rounded bg-neutral-800 px-1.5 py-0.5 text-[10px] font-semibold text-[#7BD3CE]">{l}</span>
                    ))}
                    {!EMAIL_SEQ.some(([k]) => r.emails?.[k]) && <span className="text-neutral-500">—</span>}
                  </div>
                </td>
                <td className="px-3 py-2">{yn(r.tech_issue)}</td>
                <td className="px-3 py-2">{yn(r.ticket_raised)}</td>
                <td className="px-3 py-2">{(r.ticket_types || []).map((t) => TICKET_LABELS[t] || t).join(", ") || "—"}</td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr><td colSpan={17} className="px-3 py-6 text-center text-neutral-500">No rows match</td></tr>
            )}
          </tbody>
        </table>
      </ScrollX>
    </div>
  );
}

function FunderTable({ rows, onToggleApproved, onRowClick }) {
  const [sort, setSort] = useState({ col: "created_at", dir: "desc" });
  const [q, setQ] = useState("");
  const [apprF, setApprF] = useState("");
  const filtered = useSortedFiltered(rows, sort, q,
    apprF ? (r) => String(r.approved) === apprF : null);
  return (
    <div data-testid="funders-table">
      <div className="mb-3 flex flex-wrap gap-2">
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Filter by name, email…"
          data-testid="funders-table-search"
          className="w-64 rounded-lg border border-neutral-700 bg-neutral-800 px-3 py-1.5 text-xs text-white placeholder-neutral-500 outline-none focus:border-[#0A8B85]" />
        <select value={apprF} onChange={(e) => setApprF(e.target.value)}
          data-testid="funders-table-approved-filter"
          className="rounded-lg border border-neutral-700 bg-neutral-800 px-2 py-1.5 text-xs text-white">
          <option value="">All</option>
          <option value="true">Approved</option>
          <option value="false">Not approved</option>
        </select>
        <span className="self-center text-xs text-neutral-500">{filtered.length} of {rows.length}</span>
      </div>
      <div className="overflow-x-auto rounded-xl border border-neutral-800">
        <table className="w-full text-xs">
          <thead className="bg-neutral-900">
            <tr>
              <SortHeader label="Name" col="name" sort={sort} setSort={setSort} />
              <SortHeader label="Email" col="email" sort={sort} setSort={setSort} />
              <SortHeader label="Registered" col="created_at" sort={sort} setSort={setSort} />
              <SortHeader label="Type" col="funder_type" sort={sort} setSort={setSort} />
              <SortHeader label="Criteria" col="criteria_set" sort={sort} setSort={setSort} />
              <SortHeader label="Views" col="views" sort={sort} setSort={setSort} />
              <SortHeader label="Intros" col="intros" sort={sort} setSort={setSort} />
              <SortHeader label="Approved" col="approved" sort={sort} setSort={setSort} />
              <SortHeader label="Ticket" col="ticket_raised" sort={sort} setSort={setSort} />
              <th className="px-3 py-2 text-left text-[10px] font-bold uppercase tracking-wider text-neutral-400">Ticket Type</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((r, i) => (
              <tr key={r.user_id} onClick={() => onRowClick(r)}
                  data-testid={`funders-table-row-${i}`}
                  className="cursor-pointer border-t border-neutral-800 text-neutral-300 hover:bg-neutral-800/60">
                <td className="px-3 py-2 font-semibold text-white">{r.name}</td>
                <td className="px-3 py-2">{r.email}</td>
                <td className="px-3 py-2">{(r.created_at || "").slice(0, 10)}</td>
                <td className="px-3 py-2 capitalize">{r.funder_type}</td>
                <td className="px-3 py-2">{r.criteria_set ? "Yes" : "No"}</td>
                <td className="px-3 py-2">{r.views}</td>
                <td className="px-3 py-2">{r.intros}</td>
                <td className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
                  <button type="button" onClick={() => onToggleApproved(r)}
                    data-testid={`funder-approve-toggle-${i}`}
                    className={"relative h-5 w-9 rounded-full transition-colors " + (r.approved ? "bg-emerald-500" : "bg-neutral-700")}>
                    <span className={"absolute top-0.5 h-4 w-4 rounded-full bg-white transition-all " + (r.approved ? "left-4" : "left-0.5")} />
                  </button>
                </td>
                <td className="px-3 py-2">{yn(r.ticket_raised)}</td>
                <td className="px-3 py-2">{(r.ticket_types || []).map((t) => TICKET_LABELS[t] || t).join(", ") || "—"}</td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr><td colSpan={10} className="px-3 py-6 text-center text-neutral-500">No rows match</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function AdvisorTable({ rows, onRowClick }) {
  const [sort, setSort] = useState({ col: "created_at", dir: "desc" });
  const [q, setQ] = useState("");
  const filtered = useSortedFiltered(rows, sort, q, null);
  return (
    <div data-testid="advisors-table">
      <div className="mb-3 flex flex-wrap gap-2">
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Filter by name, email…"
          data-testid="advisors-table-search"
          className="w-64 rounded-lg border border-neutral-700 bg-neutral-800 px-3 py-1.5 text-xs text-white placeholder-neutral-500 outline-none focus:border-[#0A8B85]" />
        <span className="self-center text-xs text-neutral-500">{filtered.length} of {rows.length}</span>
      </div>
      <div className="overflow-x-auto rounded-xl border border-neutral-800">
        <table className="w-full text-xs">
          <thead className="bg-neutral-900">
            <tr>
              <SortHeader label="Name" col="name" sort={sort} setSort={setSort} />
              <SortHeader label="Email" col="email" sort={sort} setSort={setSort} />
              <SortHeader label="Clients Listed" col="clients_listed" sort={sort} setSort={setSort} />
              <SortHeader label="Confirmed" col="clients_confirmed" sort={sort} setSort={setSort} />
              <SortHeader label="NDA Signed" col="nda_signed_at" sort={sort} setSort={setSort} />
              <SortHeader label="Terms Accepted" col="terms_accepted_at" sort={sort} setSort={setSort} />
              <SortHeader label="Registered" col="created_at" sort={sort} setSort={setSort} />
              <SortHeader label="Status" col="advisor_status" sort={sort} setSort={setSort} />
            </tr>
          </thead>
          <tbody>
            {filtered.map((r, i) => (
              <tr key={r.user_id} onClick={() => onRowClick(r)}
                  data-testid={`advisors-table-row-${i}`}
                  className="cursor-pointer border-t border-neutral-800 text-neutral-300 hover:bg-neutral-800/60">
                <td className="px-3 py-2 font-semibold text-white">{r.name}</td>
                <td className="px-3 py-2">{r.email}</td>
                <td className="px-3 py-2">{r.clients_listed}</td>
                <td className="px-3 py-2 font-bold text-emerald-400">{r.clients_confirmed}</td>
                <td className="px-3 py-2">{r.nda_signed_at ? `Yes · ${r.nda_signed_at.slice(0, 10)}` : "No"}</td>
                <td className="px-3 py-2">{r.terms_accepted_at ? `Yes · ${r.terms_accepted_at.slice(0, 10)}` : "No"}</td>
                <td className="px-3 py-2">{(r.created_at || "").slice(0, 10)}</td>
                <td className="px-3 py-2">
                  <span className={"rounded-full px-2 py-0.5 font-bold capitalize " +
                    (r.advisor_status === "approved" ? "bg-emerald-500/15 text-emerald-400"
                     : r.advisor_status === "deactivated" ? "bg-rose-500/15 text-rose-400"
                     : "bg-amber-500/15 text-amber-400")}>
                    {r.advisor_status}
                  </span>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr><td colSpan={8} className="px-3 py-6 text-center text-neutral-500">No advisors yet</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function AdvisorProfileModal({ userId, onClose, onChanged }) {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => api.get(`/admin/advisors/${userId}`).then((r) => setD(r.data)).catch(() => setD({ error: true }));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [userId]);

  const act = async (fn, msg) => {
    setBusy(true);
    try { await fn(); toast.success(msg); load(); onChanged(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Action failed"); }
    finally { setBusy(false); }
  };

  const a = d?.advisor;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" data-testid="advisor-profile-modal">
      <div className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-2xl border border-neutral-800 bg-neutral-900 p-6 text-neutral-200">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-bold text-white">Advisor profile</h3>
          <button onClick={onClose} data-testid="advisor-profile-close" className="rounded p-1 hover:bg-neutral-800"><X className="h-4 w-4" /></button>
        </div>
        {!d ? <p className="mt-6 flex items-center gap-2 text-sm"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</p> : d.error ? (
          <p className="mt-6 text-sm text-red-400">Couldn't load advisor.</p>
        ) : (
          <div className="mt-4 space-y-5 text-sm">
            <div>
              <p className="text-[10px] font-bold uppercase tracking-widest text-[#F59E0B]">Registration</p>
              <p className="mt-1">{a.name} · {a.email} · {a.phone || "no phone"}</p>
              <p className="text-neutral-400">
                Registered {(a.created_at || "").slice(0, 10)} · Status: <b className="capitalize text-white">{a.advisor_status || "pending"}</b>
              </p>
              <div className="mt-3 flex gap-2">
                {a.advisor_status !== "approved" && (
                  <button disabled={busy} data-testid="advisor-approve-btn"
                    onClick={() => act(() => api.post(`/admin/advisors/${userId}/approve`), "Advisor approved")}
                    className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-600 px-4 py-1.5 text-xs font-bold text-white hover:bg-emerald-500 disabled:opacity-50">
                    <Check className="h-3.5 w-3.5" /> Approve advisor
                  </button>
                )}
                {a.advisor_status !== "deactivated" && (
                  <button disabled={busy} data-testid="advisor-deactivate-btn"
                    onClick={() => { if (window.confirm("Deactivate this advisor? Removes access to ALL linked clients immediately.")) act(() => api.post(`/admin/advisors/${userId}/deactivate`), "Advisor deactivated"); }}
                    className="inline-flex items-center gap-1.5 rounded-lg bg-rose-600/90 px-4 py-1.5 text-xs font-bold text-white hover:bg-rose-500 disabled:opacity-50">
                    <X className="h-3.5 w-3.5" /> Deactivate account
                  </button>
                )}
              </div>
            </div>
            <div>
              <p className="text-[10px] font-bold uppercase tracking-widest text-[#F59E0B]">Agreements</p>
              <ul className="mt-1 space-y-1 text-xs text-neutral-300">
                {(d.agreements || []).map((g) => (
                  <li key={g.agreement_id}>
                    <b className="text-white">{g.type === "nda" ? "Platform NDA" : "Advisor Terms"}</b> — accepted {(g.accepted_at || "").slice(0, 16).replace("T", " ")} by {g.name} ({g.email}) · {g.version}
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <p className="text-[10px] font-bold uppercase tracking-widest text-[#F59E0B]">Clients ({(d.clients || []).length})</p>
              <div className="mt-2 space-y-2">
                {(d.clients || []).map((c, i) => (
                  <div key={c.link_id} className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-neutral-800 p-2.5" data-testid={`advisor-modal-client-${i}`}>
                    <div>
                      <p className="text-xs font-semibold text-white">{c.client_name} <span className="text-neutral-500">· {c.client_email}</span></p>
                      <p className="text-[11px] text-neutral-500">
                        Ack: {(c.ack?.accepted_at || "").slice(0, 16).replace("T", " ") || "—"}
                        {c.confirmed_at && ` · Confirmed ${(c.confirmed_at).slice(0, 10)}`}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={"rounded-full px-2 py-0.5 text-[10px] font-bold capitalize " +
                        (c.status === "confirmed" ? "bg-emerald-500/15 text-emerald-400"
                         : c.status === "revoked" ? "bg-rose-500/15 text-rose-400"
                         : c.status === "expired" ? "bg-neutral-700 text-neutral-400"
                         : "bg-amber-500/15 text-amber-400")}>
                        {c.status === "pending" ? "awaiting client" : c.status}
                      </span>
                      {c.status !== "revoked" && (
                        <button disabled={busy} data-testid={`advisor-revoke-client-${i}`}
                          onClick={() => act(() => api.post(`/admin/advisors/${userId}/clients/${c.link_id}/revoke`), "Client access revoked")}
                          className="rounded-lg border border-neutral-700 px-2 py-0.5 text-[10px] font-bold text-neutral-400 hover:border-rose-500 hover:text-rose-400">
                          Revoke
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
            {(d.tickets || []).length > 0 && (
              <div>
                <p className="text-[10px] font-bold uppercase tracking-widest text-[#F59E0B]">Support tickets</p>
                <ul className="mt-1 space-y-1 text-xs text-neutral-400">
                  {d.tickets.map((t) => <li key={t.ticket_id}>{t.subject} · <span className="capitalize">{t.status}</span></li>)}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function FunderProfileModal({ userId, onClose }) {
  const [d, setD] = useState(null);
  useEffect(() => {
    api.get(`/admin/dashboard/funder/${userId}`).then((r) => setD(r.data)).catch(() => setD({ error: true }));
  }, [userId]);
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" data-testid="funder-profile-modal">
      <div className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-2xl border border-neutral-800 bg-neutral-900 p-6 text-neutral-200">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-bold text-white">Funder profile</h3>
          <button onClick={onClose} data-testid="funder-profile-close" className="rounded p-1 hover:bg-neutral-800"><X className="h-4 w-4" /></button>
        </div>
        {!d ? <p className="mt-6 flex items-center gap-2 text-sm"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</p> : d.error ? (
          <p className="mt-6 text-sm text-red-400">Couldn't load profile.</p>
        ) : (
          <div className="mt-4 space-y-5 text-sm">
            <div>
              <p className="text-[10px] font-bold uppercase tracking-widest text-[#F59E0B]">Registration</p>
              <p className="mt-1">{d.user?.name} · {d.user?.email}</p>
              <p className="text-neutral-400">Org: {d.user?.organization || "—"} · Joined {(d.user?.created_at || "").slice(0, 10)} · Status: <b className="capitalize text-white">{d.user?.funder_approval_status || "pending"}</b></p>
            </div>
            <div>
              <p className="text-[10px] font-bold uppercase tracking-widest text-[#F59E0B]">Investment criteria</p>
              {d.mandate ? (
                <p className="mt-1 text-neutral-300">
                  Type: <span className="capitalize">{d.mandate.type || "—"}</span> · Sectors: {(d.mandate.sectors || []).join(", ") || "—"} · Countries: {(d.mandate.countries || []).join(", ") || "—"} · Ticket: ${Number(d.mandate.ticket_min || 0).toLocaleString()}–${Number(d.mandate.ticket_max || 0).toLocaleString()} · Confirmed: {d.mandate.confirmed_at ? "Yes" : "No"}
                </p>
              ) : <p className="mt-1 text-neutral-500">No criteria set yet.</p>}
            </div>
            <div>
              <p className="text-[10px] font-bold uppercase tracking-widest text-[#F59E0B]">Deal flow activity (latest 50)</p>
              {(d.activity || []).length === 0 ? <p className="mt-1 text-neutral-500">No activity yet.</p> : (
                <ul className="mt-1 max-h-48 space-y-1 overflow-y-auto">
                  {d.activity.map((a, i) => (
                    <li key={i} className="text-xs text-neutral-400">
                      <span className="text-neutral-200">{a.action}</span> · {a.target_id} · {(a.created_at || "").slice(0, 16).replace("T", " ")}
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div>
              <p className="text-[10px] font-bold uppercase tracking-widest text-[#F59E0B]">Support tickets</p>
              {(d.tickets || []).length === 0 ? <p className="mt-1 text-neutral-500">None raised.</p> : (
                <ul className="mt-1 space-y-2">
                  {d.tickets.map((t) => (
                    <li key={t.ticket_id} className="rounded-lg border border-neutral-800 p-2 text-xs">
                      <p className="font-semibold text-white">{t.subject} <span className="ml-1 rounded-full bg-neutral-800 px-2 py-0.5 capitalize text-neutral-300">{t.status}</span></p>
                      <p className="mt-0.5 text-neutral-400">{t.message}</p>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default function AdminDashboard() {
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [growthView, setGrowthView] = useState("all");
  const [tab, setTab] = useState("business");
  const [funderModal, setFunderModal] = useState(null);
  const [advisorModal, setAdvisorModal] = useState(null);
  const [analystLeads, setAnalystLeads] = useState([]);

  const load = () => {
    api.get("/admin/dashboard").then((r) => setData(r.data)).catch(() => {});
    api.get("/admin/analyst-support-leads").then((r) => setAnalystLeads(r.data || [])).catch(() => {});
  };
  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, []);

  const toggleApproved = async (r) => {
    try {
      if (r.approved) {
        await api.post(`/admin/funders/${r.user_id}/reject`, { reason: "Access disabled by admin from dashboard" });
      } else {
        await api.post(`/admin/funders/${r.user_id}/approve`);
      }
      toast.success(r.approved ? `${r.name} access disabled` : `${r.name} approved`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't update approval");
    }
  };

  if (!data) {
    return (
      <AdminShell>
        <p className="flex items-center gap-2 text-sm text-neutral-400" data-testid="admin-dash-loading">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading dashboard…
        </p>
      </AdminShell>
    );
  }

  const s = data.stats;
  const bandRows = Object.keys(BAND_LABELS).map((k) => ({
    band: BAND_LABELS[k], Business: data.bands.business[k] || 0, Project: data.bands.project[k] || 0,
  }));
  const totIncome = Object.values(data.income).reduce((a, b) => a + b, 0) || 1;
  const growthKey = { all: "all", business: "business", project: "project", funders: "funders" }[growthView];

  return (
    <AdminShell>
      <div className="space-y-6 pb-20" data-testid="admin-dashboard-v2">
        {/* TOP STATS BAR */}
        <div className="grid grid-cols-2 items-start gap-2 sm:grid-cols-3 lg:grid-cols-5">
          <Stat label="Total Users" value={s.total_users} testid="stat-total-users" breakdown={s.user_breakdown} />
          <Stat label="Business Founders" value={s.business_founders} testid="stat-business-founders" />
          <Stat label="Project Founders" value={s.project_founders} testid="stat-project-founders" />
          <Stat label="Funders" value={s.funders} testid="stat-funders" />
          <Stat label="New Users This Month" value={s.new_users_this_month} testid="stat-new-users" />
        </div>
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-6">
          <Stat label="CR Scores · This Month" value={s.crs.this_month} testid="stat-crs-month" />
          <Stat label="CR Scores · 3 Months" value={s.crs.last_3_months} testid="stat-crs-3mo" />
          <Stat label="CR Scores · All Time" value={s.crs.all_time} testid="stat-crs-all" />
          <Stat label="Reports · This Month" value={s.reports.this_month} testid="stat-reports-month" />
          <Stat label="Reports · 3 Months" value={s.reports.last_3_months} testid="stat-reports-3mo" />
          <Stat label="Reports · All Time" value={s.reports.all_time} testid="stat-reports-all" />
        </div>

        {/* USER GROWTH */}
        <div className={card} data-testid="growth-chart-card">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h3 className="flex items-center gap-2 text-sm font-bold text-white"><TrendingUp className="h-4 w-4 text-[#0A8B85]" /> User growth — new signups per month</h3>
            <div className="flex rounded-full border border-neutral-700 p-0.5" data-testid="growth-toggle">
              {[["all", "All Users"], ["business", "Business"], ["project", "Project"], ["funders", "Funders"]].map(([k, l]) => (
                <button key={k} onClick={() => setGrowthView(k)}
                  data-testid={`growth-toggle-${k}`}
                  className={"rounded-full px-3 py-1 text-[11px] font-bold " + (growthView === k ? "bg-[#F59E0B] text-neutral-900" : "text-neutral-400 hover:text-white")}>
                  {l}
                </button>
              ))}
            </div>
          </div>
          <div className="mt-4 h-56">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data.user_growth}>
                <CartesianGrid strokeDasharray="3 3" stroke="#262626" />
                <XAxis dataKey="month" stroke="#737373" fontSize={11} />
                <YAxis allowDecimals={false} stroke="#737373" fontSize={11} />
                <Tooltip contentStyle={{ background: "#171717", border: "1px solid #404040", fontSize: 12 }} />
                <Bar dataKey={growthKey} fill="#0A8B85" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* FUNNELS */}
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          <Funnel title="Business Founder funnel" stages={data.funnels.business} labels={BIZ_STAGES} testid="funnel-business" />
          <Funnel title="Project Founder funnel" stages={data.funnels.project} labels={PROJ_STAGES} testid="funnel-project" />
        </div>

        {/* BANDS + INCOME */}
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          <div className={card} data-testid="bands-chart-card">
            <h3 className="text-sm font-bold text-white">CRS / PRS band distribution</h3>
            <div className="mt-4 h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={bandRows}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#262626" />
                  <XAxis dataKey="band" stroke="#737373" fontSize={9} interval={0} angle={-15} textAnchor="end" height={50} />
                  <YAxis allowDecimals={false} stroke="#737373" fontSize={11} />
                  <Tooltip contentStyle={{ background: "#171717", border: "1px solid #404040", fontSize: 12 }} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Bar dataKey="Business" fill="#0A8B85" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="Project" fill="#F59E0B" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
          <div className={card} data-testid="income-card">
            <h3 className="text-sm font-bold text-white">Client tier (by amount raising) — business &amp; project founders</h3>
            <div className="mt-4 space-y-3">
              {Object.keys(INCOME_LABELS).map((k) => (
                <div key={k} data-testid={`income-${k}`}>
                  <div className="flex justify-between text-xs">
                    <span className="font-semibold text-neutral-300">{INCOME_LABELS[k]}</span>
                    <span className="text-neutral-400"><b className="text-white">{data.income[k]}</b> · {Math.round((data.income[k] / totIncome) * 100)}%</span>
                  </div>
                  <div className="mt-1 h-3 rounded-full bg-neutral-800">
                    <div className="h-full rounded-full bg-[#F59E0B]" style={{ width: `${(data.income[k] / totIncome) * 100}%` }} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* TECH ISSUES */}
        <div className={card} data-testid="tech-issues-card">
          <h3 className="flex items-center gap-2 text-sm font-bold text-white"><LifeBuoy className="h-4 w-4 text-[#0A8B85]" /> Tech issues</h3>
          <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-3">
            <Stat label="Document Upload Issues" value={data.tech_issues.document_upload} testid="tech-doc-upload" />
            <Stat label="Analysis Issues" value={data.tech_issues.ai_analysis} testid="tech-ai" />
            <Stat label="Report Download Issues" value={data.tech_issues.report_download} testid="tech-report" />
          </div>
          <p className="mt-4 text-xs text-neutral-400" data-testid="open-tickets-line">
            <b className="text-white">{data.tech_issues.open_total}</b> open support tickets
            {Object.keys(data.tech_issues.open_by_type).length > 0 && (
              <> — {Object.entries(data.tech_issues.open_by_type).map(([k, v]) => `${TICKET_LABELS[k] || k}: ${v}`).join(" · ")}</>
            )}
          </p>
        </div>

        {/* TABLES */}
        <div className={card}>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex gap-2" data-testid="user-tabs">
              {[["business", "Business Founders"], ["project", "Project Founders"], ["funders", "Funders"], ["advisors", "Advisors"]].map(([k, l]) => (
                <button key={k} onClick={() => setTab(k)} data-testid={`tab-${k}`}
                  className={"rounded-full px-4 py-1.5 text-xs font-bold " + (tab === k ? "bg-[#0A8B85] text-white" : "bg-neutral-800 text-neutral-400 hover:text-white")}>
                  {l} ({(data.tables[k] || []).length})
                </button>
              ))}
            </div>
          </div>
          <div className="mt-4">
            {tab === "business" && (
              <FounderTable rows={data.tables.business} stageLabels={BIZ_STAGES} scoreLabel="CRS"
                onRowClick={(r) => nav(`/admin/businesses/${r.company_id}`)} onDeleted={load} testid="business-table" />
            )}
            {tab === "project" && (
              <FounderTable rows={data.tables.project} stageLabels={PROJ_STAGES} scoreLabel="PRS"
                onRowClick={(r) => nav(`/admin/businesses/${r.company_id}`)} onDeleted={load} testid="project-table" />
            )}
            {tab === "funders" && (
              <FunderTable rows={data.tables.funders} onToggleApproved={toggleApproved}
                onRowClick={(r) => setFunderModal(r.user_id)} />
            )}
            {tab === "advisors" && (
              <AdvisorTable rows={data.tables.advisors || []}
                onRowClick={(r) => setAdvisorModal(r.user_id)} />
            )}
          </div>
        </div>

        {/* ANALYST SUPPORT LEADS */}
        <div className={card} data-testid="analyst-leads-section">
          <p className="text-sm font-bold uppercase tracking-wider text-neutral-400">
            Analyst Support Leads ({analystLeads.length})
          </p>
          {analystLeads.length === 0 ? (
            <p className="mt-3 text-sm text-neutral-500" data-testid="analyst-leads-empty">
              No enquiries yet — leads from /analyst-support will appear here.
            </p>
          ) : (
            <div className="mt-3 overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="border-b border-neutral-800 bg-neutral-950 text-left uppercase tracking-wider text-neutral-500">
                  <tr>
                    <th className="px-3 py-2">Contacted</th>
                    <th className="px-3 py-2">Name</th>
                    <th className="px-3 py-2">Company</th>
                    <th className="px-3 py-2">Email</th>
                    <th className="px-3 py-2">Mobile</th>
                    <th className="px-3 py-2">Tier</th>
                    <th className="px-3 py-2">Submitted</th>
                  </tr>
                </thead>
                <tbody>
                  {analystLeads.map((l) => (
                    <tr key={l.lead_id}
                        className={"border-t border-neutral-800 " + (l.contacted_at ? "text-neutral-500" : "text-neutral-300")}
                        data-testid={`analyst-lead-${l.lead_id}`}>
                      <td className="px-3 py-2">
                        <input type="checkbox" checked={!!l.contacted_at}
                          onChange={async (e) => {
                            const contacted = e.target.checked;
                            setAnalystLeads((rows) => rows.map((r) =>
                              r.lead_id === l.lead_id
                                ? { ...r, contacted_at: contacted ? new Date().toISOString() : null }
                                : r));
                            try {
                              await api.post(`/admin/analyst-support-leads/${l.lead_id}/contacted`, { contacted });
                            } catch { load(); }
                          }}
                          data-testid={`analyst-lead-contacted-${l.lead_id}`}
                          className="h-4 w-4 cursor-pointer accent-[#0A8B85]" />
                      </td>
                      <td className={"px-3 py-2 font-semibold " + (l.contacted_at ? "text-neutral-400 line-through" : "text-white")}>{l.full_name}</td>
                      <td className="px-3 py-2">{l.company_name}</td>
                      <td className="px-3 py-2">{l.email}</td>
                      <td className="px-3 py-2">{l.mobile || "—"}</td>
                      <td className="px-3 py-2">
                        <span className="rounded-full bg-[#0A8B85]/15 px-2.5 py-0.5 text-[10px] font-bold text-[#7BD3CE]">
                          {l.tier}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-neutral-400">
                        {new Date(l.created_at).toLocaleDateString("en-GB",
                          { day: "numeric", month: "short", year: "numeric" })}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {funderModal && <FunderProfileModal userId={funderModal} onClose={() => setFunderModal(null)} />}
        {advisorModal && (
          <AdvisorProfileModal userId={advisorModal} onClose={() => setAdvisorModal(null)} onChanged={load} />
        )}
      </div>
    </AdminShell>
  );
}
